// Dashboard — first surface non-technical users land on.
//
// Per business-user-surfaces theme (memory, 2026-05-12) and the
// dashboard project memo: lead with results — counts and a
// distribution — not graphs or queries. The layout draws every card's shell
// at once and each card fills in from its own request, so a slow card never
// holds the page (2026-09-10: one request for all cards held it 8.5 minutes).
//
// Phase 2 (2026-05-20) adds admin customization: PowerUser+ users
// see icons to add/edit/delete custom cards and hide individual
// built-ins per database. Regular users see the same dashboard the
// admin shaped — no affordances visible.

import { useState, type ReactNode } from 'react';
import { RunningNotice } from '@/components/RunningNotice';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';
import { Link, useNavigate } from 'react-router';
import {
  CardRequestError,
  dashboardCardQuery,
  useDashboardCard,
  useDashboardLayout,
  type Card,
  type CardShell,
  type DistributionItem,
} from '@/lib/useDashboard';
import {
  deleteCard,
  hideBuiltin,
  unhideBuiltin,
  useDashboardCards,
  type CardDef,
} from '@/lib/useDashboardCards';
import { canImportData, canManageDashboards, useMe } from '@/lib/useMe';
import { useApiSession } from '@/lib/useApiSession';
import { EditModeButton } from '@/lib/editMode';
import { ALL_GRAPHS } from '@/components/GraphSelector';
import { VdbBadge } from '@/components/VdbBadge';
import { apiFetch } from '@/lib/api';
import { cn, formatDecimalForDisplay } from '@/lib/utils';
import { DashboardCardModal } from '@/components/DashboardCardModal';
import { messageClass } from '@/components/ui/Message';
import { buttonVariants } from '@/components/ui/Button';

const BUILTIN_LABELS: Record<string, string> = {
  classes: 'Classes',
  object_properties: 'Object properties',
  datatype_properties: 'Datatype properties',
  instances_by_class: 'Instances by class',
  ontology_readiness: 'Ontology readiness',
  data_readiness: 'Data readiness',
  layer_ontology: 'Statements in the ontology layer',
  layer_vocabulary: 'Statements in the vocabulary layer',
  layer_instance: 'Statements in the instance layer',
  layer_inferred: 'Statements in the inferred layer',
  layer_alignment: 'Statements in the alignment layer',
};

export function DashboardPage() {
  const layoutQ = useDashboardLayout();
  const [loadStartedAt] = useState(() => Date.now());
  const meQ = useMe();
  const me = meQ.data;
  const [editMode, setEditMode] = useState(false);
  const canManage = canManageDashboards(me) && editMode;
  const navigate = useNavigate();

  // Load the raw card definitions only when the user can manage —
  // otherwise the GET 403s. Hooks must run unconditionally; we pass
  // `enabled` to react-query to gate the actual fetch.
  const cardsQ = useDashboardCards(canManage);
  const customDefById: Record<string, CardDef> = {};
  for (const c of cardsQ.data?.cards ?? []) {
    customDefById[c.id] = c;
  }
  const hiddenIds: string[] = cardsQ.data?.hidden_builtins ?? [];

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | undefined>(undefined);
  // Tabs come from the cards themselves: built-ins live on System,
  // custom cards on whatever dashboard name they were saved with
  // (default Reports). First slice of the dashboard-v2 multi-dashboard
  // layout.
  const [tab, setTab] = useState('System');
  const tabNames = (() => {
    const names = new Set<string>(['System']);
    for (const c of layoutQ.data ?? []) names.add(c.dashboard);
    return Array.from(names);
  })();

  const qc = useQueryClient();
  function refetchAll() {
    qc.invalidateQueries({ queryKey: ['dashboard'] });
    qc.invalidateQueries({ queryKey: ['dashboard-cards'] });
  }

  return (
    <div className="container mx-auto max-w-5xl py-6">
      <header className="mb-6 flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-tres-text-primary">Dashboard</h1>
          <p className="text-sm text-tres-text-secondary">What's in the graph right now.</p>
        </div>
        <div className="flex items-center gap-2">
          <VdbBadge />
          <EditModeButton
            on={editMode}
            onToggle={setEditMode}
            visible={canManageDashboards(me)}
          />
          {canManage && (
            <button
              type="button"
              onClick={() => {
                setEditingId(undefined);
                setModalOpen(true);
              }}
              className={buttonVariants({ variant: 'primary', size: 'md' })}
            >
              + Add card
            </button>
          )}
        </div>
      </header>

      {layoutQ.isLoading && (
        <div className="space-y-1 rounded-md border border-border bg-card p-6 text-sm text-tres-text-secondary">
          <p>Loading the dashboard’s layout…</p>
          <RunningNotice since={loadStartedAt} what="dashboard" />
        </div>
      )}
      {layoutQ.error && (
        <div className={messageClass('warning', 'normal')}>
          {(layoutQ.error as Error).message}
        </div>
      )}
      <div className="mb-4 flex gap-2 border-b border-border">
        {tabNames.map((name) => (
          <DashTab key={name} active={tab === name} onClick={() => setTab(name)}>
            {name}
            {name !== 'System' && layoutQ.data && (
              <span
                className="ml-1.5 rounded bg-muted px-1.5 text-xs text-tres-text-secondary"
                title="Cards on this dashboard"
              >
                {layoutQ.data.filter((c) => c.dashboard === name).length}
              </span>
            )}
          </DashTab>
        ))}
      </div>

      {layoutQ.data && (
        <DashboardGrid
          shells={layoutQ.data.filter((c) => c.dashboard === tab)}
          canManage={canManage}
          customDefById={customDefById}
          onEntityClick={(uri) => {
            // No graph param: the dashboard cannot know the entity's home
            // graph (a class lives in the ontology graph, an instance in
            // data/catalog), and the Entity page never read it anyway. The
            // page's own home-graph shift lands the view correctly.
            navigate(`/entity?${new URLSearchParams({ uri }).toString()}`);
          }}
          onEdit={(id) => {
            setEditingId(id);
            setModalOpen(true);
          }}
          onRefetchNeeded={refetchAll}
        />
      )}

      {canManage && hiddenIds.length > 0 && (
        <HiddenBuiltinsBar hiddenIds={hiddenIds} onRefetchNeeded={refetchAll} />
      )}

      <DashboardCardModal
        open={modalOpen}
        editing={editingId ? customDefById[editingId] : undefined}
        deletedCards={cardsQ.data?.deleted_cards ?? []}
        onClose={() => setModalOpen(false)}
        onSaved={refetchAll}
      />
    </div>
  );
}

function DashTab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        '-mb-px border-b-2 px-3 py-1.5 text-sm font-medium',
        active
          ? 'border-tres-action text-tres-text-primary'
          : 'border-transparent text-tres-text-secondary hover:text-tres-text-primary',
      )}
    >
      {children}
    </button>
  );
}

function DashboardGrid({
  shells,
  canManage,
  customDefById,
  onEntityClick,
  onEdit,
  onRefetchNeeded,
}: {
  shells: CardShell[];
  canManage: boolean;
  customDefById: Record<string, CardDef>;
  onEntityClick: (uri: string) => void;
  onEdit: (id: string) => void;
  onRefetchNeeded: () => void;
}) {
  const session = useApiSession();
  const canLoad = canImportData(useMe().data);
  const hasTriples = shells.some((s) => s.id === 'triples');
  const triplesQ = useQuery({
    ...dashboardCardQuery('triples', session),
    enabled: session.ready && hasTriples,
  });

  if (shells.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-border bg-card/50 p-8 text-center text-sm text-tres-text-secondary">
        No cards on this tab yet. Use + Add card with the Table type to create a report.
      </div>
    );
  }

  const noData =
    hasTriples && triplesQ.data?.kind === 'count' && triplesQ.data.value === 0;
  if (noData) {
    return (
      <div className="rounded-md border border-dashed border-border bg-card/50 p-8 text-center text-sm text-tres-text-secondary">
        <p>No data loaded yet.</p>
        <p className="mt-2">
          {canLoad ? (
            <>
              <Link to="/admin/database" className={buttonVariants({ variant: 'link', size: 'md' })}>
                Load some data
              </Link>{' '}
              to see counts and distributions here.
            </>
          ) : (
            // Read-only roles get the fact, not a link into a permission
            // wall (the Database page refuses non-ImportData roles).
            <>Counts and distributions appear here once data is loaded.</>
          )}
        </p>
      </div>
    );
  }

  // Built-in cards carry a `group`. Every "… layer" group folds into ONE
  // full-width layer card with a layer picker (2026-09-10: a card per layer
  // stranded each one alone on its own row); other groups (Overall) stay
  // sections; custom cards (no group) keep the by-kind sections on their tab.
  const isLayerGroup = (g: string | undefined) => !!g && g.endsWith(' layer');
  const layerCounts = shells.filter((s) => s.kind === 'count' && isLayerGroup(s.group));
  const layerDistributions = shells.filter(
    (s) => s.kind === 'distribution' && isLayerGroup(s.group),
  );
  const grouped = shells.filter((s) => s.group && !isLayerGroup(s.group) && s.kind !== 'table');
  const groupRank = (g: string) => (g === 'Admin' ? 0 : g === 'Overall' ? 1 : 2);
  const groupNames = Array.from(new Set(grouped.map((s) => s.group ?? ''))).sort(
    (a, b) => groupRank(a) - groupRank(b),
  );
  const ungrouped = shells.filter((s) => !s.group);
  const byKind = (kind: Card['kind']) => ungrouped.filter((s) => s.kind === kind);
  const tables = shells.filter((s) => s.kind === 'table');
  const readiness = [...byKind('ontology_readiness'), ...byKind('data_readiness')];

  // Section headers only earn their place when there is more than one section
  // on the tab: a custom dashboard holding a single table card should not
  // render under a lone "REPORTS" banner (UAT 2026-07-20 — it read as a stuck
  // page state, not a section label).
  const sectionCount =
    groupNames.length +
    (layerCounts.length + layerDistributions.length > 0 ? 1 : 0) +
    (readiness.length > 0 ? 1 : 0) +
    (byKind('count').length > 0 ? 1 : 0) +
    (byKind('distribution').length > 0 ? 1 : 0) +
    (tables.length > 0 ? 1 : 0);
  const header = (label: string) =>
    sectionCount > 1 ? (
      <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-tres-text-secondary">
        {label}
      </h2>
    ) : null;

  const views = { canManage, customDefById, onEntityClick, onEdit, onRefetchNeeded };

  return (
    <div className="space-y-6">
      {groupNames.map((g) => (
        <GroupSection
          key={g}
          header={header(g)}
          shells={grouped.filter((s) => s.group === g)}
          views={views}
        />
      ))}

      {layerCounts.length + layerDistributions.length > 0 && (
        <section>
          {header('By layer')}
          {layerCounts.length > 0 && (
            <LayerCard
              shells={layerCounts}
              canManage={canManage}
              onRefetchNeeded={onRefetchNeeded}
            />
          )}
          {layerDistributions.length > 0 && (
            <div className="mt-3 space-y-3">
              {layerDistributions.map((s) => (
                <CardSlot key={s.id} shell={s} views={views} />
              ))}
            </div>
          )}
        </section>
      )}

      {/* Custom cards (no group): by-kind sections. */}
      {readiness.length > 0 && (
        <section>
          {header('Readiness')}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {readiness.map((s) => (
              <CardSlot key={s.id} shell={s} views={views} />
            ))}
          </div>
        </section>
      )}
      {byKind('count').length > 0 && (
        <section>
          {header('Counts')}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {byKind('count').map((s) => (
              <CardSlot key={s.id} shell={s} views={views} />
            ))}
          </div>
        </section>
      )}
      {byKind('distribution').length > 0 && (
        <section>
          {header('Distributions')}
          <div className="space-y-3">
            {byKind('distribution').map((s) => (
              <CardSlot key={s.id} shell={s} views={views} />
            ))}
          </div>
        </section>
      )}
      {tables.length > 0 && (
        <section>
          {header('Reports')}
          <div className="space-y-3">
            {tables.map((s) => (
              <CardSlot key={s.id} shell={s} views={views} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

interface CardViews {
  canManage: boolean;
  customDefById: Record<string, CardDef>;
  onEntityClick: (uri: string) => void;
  onEdit: (id: string) => void;
  onRefetchNeeded: () => void;
}

/** One grouped section. It asks for its cards together with the cards
 *  themselves (the same queries, fetched once), and drops out only when every
 *  card has answered with nothing. */
function GroupSection({
  header,
  shells,
  views,
}: {
  header: ReactNode;
  shells: CardShell[];
  views: CardViews;
}) {
  const session = useApiSession();
  const results = useQueries({
    queries: shells.map((s) => dashboardCardQuery(s.id, session)),
  });
  if (results.every((r) => r.isSuccess && r.data === null)) return null;
  const admin = shells.filter((s) => s.kind === 'admin');
  const small = shells.filter(
    (s) =>
      s.kind === 'ontology_readiness' ||
      s.kind === 'data_readiness' ||
      s.kind === 'count' ||
      s.kind === 'shape',
  );
  const distributions = shells.filter((s) => s.kind === 'distribution');
  return (
    <section>
      {header}
      {admin.length > 0 && (
        <div className="space-y-3">
          {admin.map((s) => (
            <CardSlot key={s.id} shell={s} views={views} />
          ))}
        </div>
      )}
      {small.length > 0 && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {small.map((s) => (
            <CardSlot key={s.id} shell={s} views={views} />
          ))}
        </div>
      )}
      {distributions.length > 0 && (
        <div className="mt-3 space-y-3">
          {distributions.map((s) => (
            <CardSlot key={s.id} shell={s} views={views} />
          ))}
        </div>
      )}
    </section>
  );
}

/** A card's own request: its shell while the server works, then the card. */
function CardSlot({ shell, views }: { shell: CardShell; views: CardViews }) {
  const q = useDashboardCard(shell.id);
  const [since] = useState(() => Date.now());
  if (q.error) {
    return (
      <div className={messageClass('platform', 'normal', 'warning')}>
        <div className="text-sm font-medium text-tres-text-primary">{shell.title}</div>
        <div className="text-xs text-tres-warning">{(q.error as Error).message}</div>
      </div>
    );
  }
  if (q.data === undefined) {
    // A card that answers 503 says what it is waiting for; it keeps asking.
    const waitingFor =
      q.failureReason instanceof CardRequestError && q.failureReason.status === 503
        ? q.failureReason.message
        : null;
    return (
      <div className="rounded-lg border border-border bg-card p-4" aria-busy="true">
        <div className="text-3xl font-semibold tabular-nums text-tres-text-secondary">…</div>
        <div className="mt-1 text-sm font-medium text-tres-text-primary">{shell.title}</div>
        <div className="text-xs text-tres-text-secondary">{shell.subtitle}</div>
        <div className="mt-2 space-y-1">
          {waitingFor && <p className="text-xs text-tres-text-secondary">{waitingFor}</p>}
          <RunningNotice since={since} />
        </div>
      </div>
    );
  }
  const card = q.data;
  if (card === null) return null;
  const customDef = views.customDefById[card.id];
  switch (card.kind) {
    case 'count':
      return (
        <CountCardView
          card={card}
          canManage={views.canManage}
          customDef={customDef}
          onEdit={views.onEdit}
          onRefetchNeeded={views.onRefetchNeeded}
        />
      );
    case 'distribution':
      return (
        <DistributionCardView
          card={card}
          canManage={views.canManage}
          customDef={customDef}
          onClick={views.onEntityClick}
          onEdit={views.onEdit}
          onRefetchNeeded={views.onRefetchNeeded}
        />
      );
    case 'table':
      return (
        <TableCardView
          card={card}
          canManage={views.canManage}
          customDef={customDef}
          onEntityClick={views.onEntityClick}
          onEdit={views.onEdit}
          onRefetchNeeded={views.onRefetchNeeded}
        />
      );
    case 'shape':
      return (
        <ShapeCardView
          card={card}
          canManage={views.canManage}
          onRefetchNeeded={views.onRefetchNeeded}
        />
      );
    case 'ontology_readiness':
    case 'data_readiness':
      return (
        <ReadinessCardView
          card={card}
          canManage={views.canManage}
          onRefetchNeeded={views.onRefetchNeeded}
        />
      );
    case 'admin':
      return <AdminCardView card={card} />;
  }
}

/** The state database writes UTC as "YYYY-MM-DD HH:MM:SS"; show it in the
 *  reader's own time. */
function stateDbTime(ts: string | null): string | null {
  if (!ts) return null;
  const d = new Date(`${ts.replace(' ', 'T')}Z`);
  return Number.isNaN(d.getTime()) ? ts : d.toLocaleString();
}

/** The site at a glance for Admins and Owners: one full-width card, four
 *  figures side by side. */
function AdminCardView({ card }: { card: Extract<Card, { kind: 'admin' }> }) {
  const figure = (value: string, label: string, detail: string | null) => (
    <div className="min-w-0">
      <div className="truncate text-xl font-semibold text-tres-text-primary" title={value}>
        {value}
      </div>
      <div className="text-sm font-medium text-tres-text-primary">{label}</div>
      {detail && <div className="text-xs text-tres-text-secondary">{detail}</div>}
    </div>
  );
  const lastCall = stateDbTime(card.api_last_call_at);
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <div className="text-sm font-medium text-tres-text-primary">{card.title}</div>
        <div className="text-xs text-tres-text-secondary">{card.subtitle}</div>
      </div>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {figure(
          stateDbTime(card.last_updated_at) ?? 'No changes recorded',
          'Last updated',
          card.last_update,
        )}
        {figure(
          card.active_now.toLocaleString(),
          'Active users',
          `in the last 15 min · ${card.active_today.toLocaleString()} in the last 24 h`,
        )}
        {figure(
          card.api_accounts_24h.toLocaleString(),
          'API inbound',
          lastCall
            ? `service accounts in the last 24 h · last call ${lastCall}`
            : 'no service account has called the API',
        )}
        {figure(
          card.model ?? 'No model configured',
          'On-board model',
          card.model_provider
            ? `${card.model_provider} · set by ${card.model_source === 'site' ? 'this site' : 'the operator'}`
            : null,
        )}
      </div>
    </div>
  );
}

// Exported for its test: a press on a card control must answer on the card.
// eslint-disable-next-line react-refresh/only-export-components -- a page component, exported to be pressed in a test
export function CardActions({
  cardId,
  isCustom,
  onEdit,
  onRefetchNeeded,
}: {
  cardId: string;
  isCustom: boolean;
  onEdit: (id: string) => void;
  onRefetchNeeded: () => void;
}) {
  const auth = useAuth();
  const [busy, setBusy] = useState(false);
  // The server's answer is read, and a refusal prints on the card it concerns:
  // both writes used to be awaited and discarded, so a failed hide or remove
  // looked exactly like a press that did nothing (assessment, 2026-09-21).
  const [problem, setProblem] = useState<string | null>(null);

  async function run(write: (token: string) => ReturnType<typeof hideBuiltin>, verb: string) {
    const token = auth.user?.id_token;
    if (!token) return setProblem(`Sign in again to ${verb} this card.`);
    setBusy(true);
    setProblem(null);
    const r = await write(token);
    setBusy(false);
    if (r.ok) onRefetchNeeded();
    else setProblem(`This card was not ${verb === 'hide' ? 'hidden' : 'removed'}: ${r.message}`);
  }

  const handleHide = () => run((token) => hideBuiltin(cardId, token), 'hide');

  function handleDelete() {
    if (!window.confirm('Remove this card? You can restore it later from + Add card.')) return;
    void run((token) => deleteCard(cardId, token), 'remove');
  }

  return (
    <div className="flex items-center gap-1">
      {isCustom && (
        <>
          <button
            type="button"
            onClick={() => onEdit(cardId)}
            disabled={busy}
            className={buttonVariants({ variant: 'ghost', size: 'icon' })}
            title="Edit card"
            aria-label="Edit card"
          >
            ✎
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={busy}
            className={buttonVariants({ variant: 'ghost', size: 'icon' })}
            title="Remove card (restorable from + Add card)"
            aria-label="Remove card"
          >
            ✕
          </button>
        </>
      )}
      {!isCustom && (
        <button
          type="button"
          onClick={handleHide}
          disabled={busy}
          className={buttonVariants({ variant: 'ghost', size: 'icon' })}
          title="Hide this built-in card (restore from the hidden list below)"
          aria-label="Hide card"
        >
          ✕
        </button>
      )}
      {problem && (
        <span role="alert" className={messageClass('warning', 'compact')}>
          {problem}
        </span>
      )}
    </div>
  );
}

/** Every layer's counts in one full-width card: a picker chooses the layer,
 *  and its numbers sit side by side. All layers load in the background, so
 *  switching is instant; a layer with no statements is not offered. */
function LayerCard({
  shells,
  canManage,
  onRefetchNeeded,
}: {
  shells: CardShell[];
  canManage: boolean;
  onRefetchNeeded: () => void;
}) {
  const session = useApiSession();
  const layers = Array.from(new Set(shells.map((s) => s.group ?? '')));
  const totalShell = (layer: string) =>
    shells.find((s) => s.group === layer && s.id.startsWith('layer_'));
  const totals = useQueries({
    queries: layers.map((l) => {
      const total = totalShell(l);
      return {
        ...dashboardCardQuery(total?.id ?? '', session),
        enabled: session.ready && !!total,
      };
    }),
  });
  const [chosen, setChosen] = useState<string | null>(null);
  const present = layers.filter((l, i) => {
    const r = totals[i];
    return !(totalShell(l) && r?.isSuccess && r.data === null);
  });
  if (present.length === 0) return null;
  const current = chosen !== null && present.includes(chosen) ? chosen : (present[0] ?? '');
  const optionLabel = (layer: string) => {
    const d = totals[layers.indexOf(layer)]?.data;
    const name = layer.replace(/ layer$/, '');
    return d?.kind === 'count' ? `${name} · ${d.value.toLocaleString()} statements` : name;
  };
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <label htmlFor="dashboard-layer" className="text-sm font-medium text-tres-text-primary">
          Layer
        </label>
        <select
          id="dashboard-layer"
          value={current}
          onChange={(e) => setChosen(e.target.value)}
          className="rounded-md border border-input bg-card px-2 py-1.5 text-sm text-tres-text-primary"
        >
          {present.map((l) => (
            <option key={l} value={l}>
              {optionLabel(l)}
            </option>
          ))}
        </select>
      </div>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {shells
          .filter((s) => s.group === current)
          .map((s) => (
            <LayerStat
              key={s.id}
              shell={s}
              canManage={canManage}
              onRefetchNeeded={onRefetchNeeded}
            />
          ))}
      </div>
    </div>
  );
}

function LayerStat({
  shell,
  canManage,
  onRefetchNeeded,
}: {
  shell: CardShell;
  canManage: boolean;
  onRefetchNeeded: () => void;
}) {
  const auth = useAuth();
  const q = useDashboardCard(shell.id);
  const [since] = useState(() => Date.now());
  // Hideable built-ins; the restore bar brings them back.
  const hide = async () => {
    const token = auth.user?.id_token;
    if (!token) return;
    await hideBuiltin(shell.id, token);
    onRefetchNeeded();
  };
  if (q.data === null) return null;
  const c = q.data?.kind === 'count' ? q.data : undefined;
  const failed = c?.subtitle === 'query failed' || !!q.error;
  // The picker names the layer, so a statement count drops "in the … layer".
  const subtitle = (c?.subtitle ?? shell.subtitle).replace(/^in the \w+ layer,?\s*/, '');
  return (
    <div className="min-w-0">
      <div className="flex items-start justify-between gap-2">
        <div
          className={cn(
            'text-2xl font-semibold tabular-nums',
            failed ? 'text-tres-warning' : !c && 'text-tres-text-secondary',
          )}
        >
          {c ? c.value.toLocaleString() : q.error ? 'failed' : '…'}
        </div>
        {canManage && (
          <button
            type="button"
            onClick={() => void hide()}
            title="Hide this number (restore from the hidden-cards bar)"
            className={buttonVariants({ variant: 'ghost', size: 'icon' })}
          >
            ×
          </button>
        )}
      </div>
      <div className="text-sm font-medium text-tres-text-primary">{shell.title}</div>
      {subtitle && subtitle !== 'query failed' && (
        <div className="text-xs text-tres-text-secondary">{subtitle}</div>
      )}
      {!c && !q.error && <RunningNotice since={since} />}
    </div>
  );
}

function CountCardView({
  card,
  canManage,
  customDef,
  onEdit,
  onRefetchNeeded,
}: {
  card: Extract<Card, { kind: 'count' }>;
  canManage: boolean;
  customDef?: CardDef | undefined;
  onEdit: (id: string) => void;
  onRefetchNeeded: () => void;
}) {
  const failed = card.subtitle === 'query failed';
  return (
    <div
      className={cn(
        'rounded-lg border bg-card p-4',
        failed ? 'border-tres-warning/40' : 'border-border',
      )}
    >
      <div className="flex items-start justify-between">
        <div className={cn('text-3xl font-semibold tabular-nums', failed && 'text-tres-warning')}>
          {card.value.toLocaleString()}
        </div>
        {canManage && (
          <CardActions
            cardId={card.id}
            isCustom={!!card.custom}
            onEdit={onEdit}
            onRefetchNeeded={onRefetchNeeded}
          />
        )}
      </div>
      <div className="mt-1 text-sm font-medium text-tres-text-primary">{card.title}</div>
      <div
        className={cn(
          'text-xs',
          failed ? 'text-tres-warning' : 'text-tres-text-secondary',
        )}
      >
        {card.subtitle}
      </div>
      {canManage && customDef && (
        <ShowSparqlToggle sparql={customDef.sparql} />
      )}
    </div>
  );
}

/** How wide the data is: statements per entity, split into the values a thing
 *  carries and the links it has, over the three counts they come from. */
export function ShapeCardView({
  card,
  canManage,
  onRefetchNeeded,
}: {
  card: Extract<Card, { kind: 'shape' }>;
  canManage: boolean;
  onRefetchNeeded: () => void;
}) {
  const per = (n: number) => (card.entities > 0 ? n / card.entities : 0);
  const values = Math.max(card.statements - card.relationships, 0);
  const one = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 1 });
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-start justify-between">
        <div className="text-3xl font-semibold tabular-nums">{one(per(card.statements))}</div>
        {canManage && (
          <CardActions
            cardId={card.id}
            isCustom={!!card.custom}
            onEdit={() => undefined}
            onRefetchNeeded={onRefetchNeeded}
          />
        )}
      </div>
      <div className="mt-1 text-sm font-medium text-tres-text-primary">{card.title}</div>
      <div className="text-xs text-tres-text-secondary">{card.subtitle}</div>
      <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
        <dt className="text-tres-text-secondary">Values per entity</dt>
        <dd className="tabular-nums text-tres-text-primary">{one(per(values))}</dd>
        <dt className="text-tres-text-secondary">Relationships per entity</dt>
        <dd className="tabular-nums text-tres-text-primary">{one(per(card.relationships))}</dd>
        <dt className="text-tres-text-secondary">Statements</dt>
        <dd className="tabular-nums text-tres-text-primary">{card.statements.toLocaleString()}</dd>
        <dt className="text-tres-text-secondary">Entities</dt>
        <dd className="tabular-nums text-tres-text-primary">{card.entities.toLocaleString()}</dd>
        <dt className="text-tres-text-secondary">Relationships</dt>
        <dd className="tabular-nums text-tres-text-primary">
          {card.relationships.toLocaleString()}
        </dd>
      </dl>
    </div>
  );
}

/// A readiness headline card: Ontology readiness (the share of a question set
/// the ontology can answer) or Data readiness (facts that do not conflict with
/// the ontology), each a click-through into the Readiness panel. QROS only.
/// Hideable in edit mode like the other built-ins. Exported for its test.
export function ReadinessCardView({
  card,
  canManage,
  onRefetchNeeded,
}: {
  card: Extract<Card, { kind: 'ontology_readiness' | 'data_readiness' }>;
  canManage: boolean;
  onRefetchNeeded: () => void;
}) {
  const auth = useAuth();
  const [problem, setProblem] = useState<string | null>(null);
  const fraction = card.fraction;
  const needs = (n: number, one: string, many: string) =>
    n > 0 ? ` · ${n} ${n === 1 ? one : many} required` : '';
  const detail =
    card.kind === 'ontology_readiness'
      ? card.fraction === null
        ? 'Not scored yet: run a question set through Can it answer your questions? on Readiness.'
        : `${card.answerable} of ${card.questions} questions in ${card.bank_name ?? 'the set'} answerable` +
          needs(card.terms_required, 'class or property', 'classes or properties') +
          needs(card.rules_required, 'rule', 'rules')
      : card.conflicting === 0
        ? `None of ${card.statements_checked.toLocaleString()} facts conflict with the ontology`
        : `${card.conflicting.toLocaleString()} of ${card.statements_checked.toLocaleString()} facts conflict with the ontology`;
  const color =
    fraction === null
      ? 'text-tres-text-secondary'
      : fraction >= 0.99
        ? 'text-tres-success'
        : fraction >= 0.9
          ? 'text-tres-text-primary'
          : 'text-tres-warning';
  return (
    <Link
      to="/ontology?readiness=1"
      className="block rounded-lg border border-border bg-card p-4 transition-colors hover:border-tres-action"
    >
      <div className="flex items-start justify-between">
        <div className={cn('text-3xl font-semibold tabular-nums', color)}>
          {fraction === null ? '—' : `${Math.round(fraction * 100)}%`}
        </div>
        {canManage && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              const token = auth.user?.id_token;
              if (!token) return setProblem('Sign in again to hide this card.');
              setProblem(null);
              void hideBuiltin(card.id, token).then((r) =>
                r.ok ? onRefetchNeeded() : setProblem(`This card was not hidden: ${r.message}`),
              );
            }}
            title="Hide this card (restore from the hidden-cards bar)"
            className={buttonVariants({ variant: 'ghost', size: 'icon' })}
          >
            ×
          </button>
        )}
      </div>
      {problem && (
        <p role="alert" className={cn(messageClass('warning', 'compact'), 'mt-2')}>
          {problem}
        </p>
      )}
      <div className="mt-1 text-sm font-medium text-tres-text-primary">{card.title}</div>
      <div className="text-xs text-tres-text-secondary">{detail}</div>
    </Link>
  );
}

/// Bars shown while a distribution card is collapsed; matches the
/// server's DISTRIBUTION_LIMIT so collapsed view == dashboard payload.
const DIST_COLLAPSED = 10;
/// Bars per page once expanded.
const DIST_PAGE = 15;
/// Expanded fetch cap — mirrors the server's TABLE_LIMIT backstop.
const DIST_EXPAND_LIMIT = 1000;

function DistributionCardView({
  card,
  canManage,
  customDef,
  onClick,
  onEdit,
  onRefetchNeeded,
}: {
  card: Extract<Card, { kind: 'distribution' }>;
  canManage: boolean;
  customDef?: CardDef | undefined;
  onClick: (uri: string) => void;
  onEdit: (id: string) => void;
  onRefetchNeeded: () => void;
}) {
  const auth = useAuth();
  // Non-null while expanded: the full item set being paged through.
  const [expandedItems, setExpandedItems] = useState<DistributionItem[] | null>(null);
  const [expandBusy, setExpandBusy] = useState(false);
  const [page, setPage] = useState(0);

  const failed = card.subtitle === 'query failed';
  const baseItems = card.items;

  function collapse() {
    setExpandedItems(null);
    setPage(0);
  }

  async function handleExpand() {
    if (card.id === 'instances_by_class') {
      // Built-in is SPARQL-capped server-side; fetch the full distribution.
      // The Dashboard is cross-graph by design (GRAPH_SELECTION_SURFACES.md),
      // so the scope sent here is the union — the same view the card's own
      // payload ships — stated explicitly rather than left to a default.
      const token = auth.user?.id_token;
      if (!token) return;
      setExpandBusy(true);
      const r = await apiFetch<{ items: DistributionItem[] }>(
        `/api/v1/dashboard/distribution?graph=${encodeURIComponent(ALL_GRAPHS)}&limit=${DIST_EXPAND_LIMIT}`,
        token,
      );
      setExpandBusy(false);
      if (r.ok) {
        setExpandedItems(r.data.items);
        setPage(0);
      }
      return;
    }
    // Custom cards already ship their full (backstop-capped) set.
    setExpandedItems(baseItems);
    setPage(0);
  }

  if (failed || card.items.length === 0) {
    return (
      <div
        className={cn(
          'rounded-lg border bg-card p-4',
          failed ? 'border-tres-warning/40' : 'border-border',
        )}
      >
        <div className="flex items-start justify-between">
          <div className="text-sm font-medium text-tres-text-primary">{card.title}</div>
          {canManage && (
            <CardActions
              cardId={card.id}
              isCustom={!!card.custom}
              onEdit={onEdit}
              onRefetchNeeded={onRefetchNeeded}
            />
          )}
        </div>
        <div
          className={cn(
            'mt-1 text-xs',
            failed ? 'text-tres-warning' : 'text-tres-text-secondary',
          )}
        >
          {card.subtitle === 'query failed'
            ? 'Query failed — see server logs.'
            : 'No instances in the store.'}
        </div>
        {canManage && customDef && <ShowSparqlToggle sparql={customDef.sparql} />}
      </div>
    );
  }

  const isExpanded = expandedItems !== null;
  const fullSet = expandedItems ?? baseItems;
  const pageCount = Math.max(1, Math.ceil(fullSet.length / DIST_PAGE));
  const curPage = Math.min(page, pageCount - 1);
  const shown = isExpanded
    ? fullSet.slice(curPage * DIST_PAGE, (curPage + 1) * DIST_PAGE)
    : baseItems.slice(0, DIST_COLLAPSED);
  // Bars stay proportional to the whole set, not the visible page.
  const maxCount = fullSet.reduce((m, it) => Math.max(m, it.count), 0);
  const hasMore = card.truncated || baseItems.length > DIST_COLLAPSED;

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="mb-1 flex flex-wrap items-baseline justify-between gap-2">
        <div className="text-sm font-medium text-tres-text-primary">{card.title}</div>
        <div className="flex items-center gap-3">
          <div className="text-xs text-tres-text-secondary">
            {card.subtitle}
            {` · ${isExpanded ? `${fullSet.length} items` : `${shown.length} shown`}`}
          </div>
          {canManage && (
            <CardActions
              cardId={card.id}
              isCustom={!!card.custom}
              onEdit={onEdit}
              onRefetchNeeded={onRefetchNeeded}
            />
          )}
        </div>
      </div>
      <BarList items={shown} max={maxCount} onClick={onClick} />
      <div className="mt-2 flex items-center justify-end gap-2 text-xs text-tres-text-secondary">
        {!isExpanded && hasMore && (
          <button
            type="button"
            onClick={() => void handleExpand()}
            disabled={expandBusy}
            className={buttonVariants({ variant: 'link', size: 'md' })}
          >
            {expandBusy
              ? 'Loading…'
              : card.id === 'instances_by_class'
                ? 'Show all'
                : `Show all ${baseItems.length.toLocaleString()}${card.truncated ? '+' : ''}`}
          </button>
        )}
        {isExpanded && (
          <>
            {pageCount > 1 && (
              <>
                <button
                  type="button"
                  disabled={curPage === 0}
                  onClick={() => setPage(curPage - 1)}
                  className="rounded border border-input px-1.5 py-0.5 disabled:opacity-40"
                >
                  Prev
                </button>
                <span>
                  page {curPage + 1} / {pageCount}
                </span>
                <button
                  type="button"
                  disabled={curPage >= pageCount - 1}
                  onClick={() => setPage(curPage + 1)}
                  className="rounded border border-input px-1.5 py-0.5 disabled:opacity-40"
                >
                  Next
                </button>
              </>
            )}
            <button
              type="button"
              onClick={collapse}
              className={buttonVariants({ variant: 'link', size: 'md' })}
            >
              Collapse
            </button>
          </>
        )}
      </div>
      {canManage && customDef && <ShowSparqlToggle sparql={customDef.sparql} />}
    </div>
  );
}

function TableCardView({
  card,
  canManage,
  customDef,
  onEntityClick,
  onEdit,
  onRefetchNeeded,
}: {
  card: Extract<Card, { kind: 'table' }>;
  canManage: boolean;
  customDef?: CardDef | undefined;
  onEntityClick: (uri: string) => void;
  onEdit: (id: string) => void;
  onRefetchNeeded: () => void;
}) {
  const failed = card.subtitle === 'query failed';
  const PAGE = 15;
  const [page, setPage] = useState(0);
  const pageCount = Math.max(1, Math.ceil(card.rows.length / PAGE));
  const cur = Math.min(page, pageCount - 1);
  const visible = card.rows.slice(cur * PAGE, (cur + 1) * PAGE);
  return (
    <div
      className={cn(
        'rounded-lg border bg-card p-4',
        failed ? 'border-tres-warning/40' : 'border-border',
      )}
    >
      <div className="mb-1 flex items-baseline justify-between">
        <div className="text-sm font-medium text-tres-text-primary">{card.title}</div>
        <div className="flex items-center gap-3">
          <div
            className={cn('text-xs', failed ? 'text-tres-warning' : 'text-tres-text-secondary')}
          >
            {failed ? 'Query failed — see server logs.' : card.subtitle}
            {!failed && ` · ${card.rows.length.toLocaleString()} rows`}
            {card.truncated && ' (capped at 1,000)'}
          </div>
          {canManage && (
            <CardActions
              cardId={card.id}
              isCustom={!!card.custom}
              onEdit={onEdit}
              onRefetchNeeded={onRefetchNeeded}
            />
          )}
        </div>
      </div>
      {card.rows.length === 0 && !failed && (
        <div className="text-xs text-tres-text-secondary">No rows.</div>
      )}
      {card.rows.length > 0 && (
        <div>
          <table className="w-full border-collapse text-xs">
            <thead>
              <tr className="border-b border-border text-left text-tres-text-secondary">
                {card.columns.map((c) => (
                  <th key={c} className="py-1 pr-3 font-medium">
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map((row, i) => (
                <tr key={cur * PAGE + i} className="border-b border-border/50 align-top">
                  {row.map((cell, j) => (
                    <td key={j} className="py-1 pr-3 text-tres-text-primary">
                      <CellRender value={cell} onEntityClick={onEntityClick} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {pageCount > 1 && (
            <div className="mt-2 flex items-center justify-end gap-2 text-xs text-tres-text-secondary">
              <button
                type="button"
                disabled={cur === 0}
                onClick={() => setPage(cur - 1)}
                className="rounded border border-input px-1.5 py-0.5 disabled:opacity-40"
              >
                Prev
              </button>
              <span>
                page {cur + 1} / {pageCount}
              </span>
              <button
                type="button"
                disabled={cur >= pageCount - 1}
                onClick={() => setPage(cur + 1)}
                className="rounded border border-input px-1.5 py-0.5 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
      {canManage && customDef && <ShowSparqlToggle sparql={customDef.sparql} />}
    </div>
  );
}

function CellRender({
  value,
  onEntityClick,
}: {
  value: string;
  onEntityClick: (uri: string) => void;
}) {
  // Entity link shapes: full IRIs AND the QROS clean-identifier forms
  // (`:Product`, `prefix:local`) the native engine binds — those rendered as
  // inert text while the same card on RDF linked (2026-08-06 audit D15).
  const curie = /^([A-Za-z][\w-]*)?:[\w][\w.-]*$/.test(value);
  if (
    value.startsWith('http://') ||
    value.startsWith('https://') ||
    value.startsWith('urn:') ||
    curie
  ) {
    const local = curie ? value.split(':').pop() || value : value.split(/[#/]/).pop() || value;
    return (
      <button
        type="button"
        onClick={() => onEntityClick(value)}
        className={buttonVariants({ variant: 'link', size: 'md' })}
        title={value}
      >
        {local}
      </button>
    );
  }
  return <>{formatDecimalForDisplay(value)}</>;
}

function ShowSparqlToggle({ sparql }: { sparql: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-3 border-t border-border pt-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={buttonVariants({ variant: 'ghost', size: 'icon' })}
      >
        {open ? 'Hide query' : 'Show query'}
      </button>
      {open && (
        <pre className="mt-2 max-h-48 overflow-auto rounded bg-muted p-2 font-mono text-[11px] text-tres-text-primary">
          {sparql}
        </pre>
      )}
    </div>
  );
}

function HiddenBuiltinsBar({
  hiddenIds,
  onRefetchNeeded,
}: {
  hiddenIds: string[];
  onRefetchNeeded: () => void;
}) {
  const auth = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  async function handleUnhide(id: string) {
    const token = auth.user?.id_token;
    if (!token) return;
    setBusy(true);
    await unhideBuiltin(id, token);
    setBusy(false);
    onRefetchNeeded();
  }

  return (
    <div className="mt-6 rounded-md border border-dashed border-border bg-card/50 p-3 text-sm">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={buttonVariants({ variant: 'ghost', size: 'icon' })}
      >
        {open ? 'Hide' : 'Show'} hidden built-ins ({hiddenIds.length})
      </button>
      {open && (
        <ul className="mt-2 space-y-1">
          {hiddenIds.map((id) => (
            <li key={id} className="flex items-center justify-between">
              <span className="text-tres-text-secondary">
                {BUILTIN_LABELS[id] ?? id}
              </span>
              <button
                type="button"
                onClick={() => handleUnhide(id)}
                disabled={busy}
                className={buttonVariants({ variant: 'link', size: 'sm' })}
              >
                Restore
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Exported for its test: a row without an entity behind it (a rule set) is text, not a button.
export function BarList({
  items,
  max: maxProp,
  onClick,
}: {
  items: DistributionItem[];
  /** Scale bars against this count (e.g. the full set's max when only
   *  one page is visible). Defaults to the visible items' max. */
  max?: number;
  onClick: (uri: string) => void;
}) {
  const max = maxProp ?? items.reduce((m, it) => Math.max(m, it.count), 0);
  if (max === 0) return null;
  return (
    <ul className="space-y-1.5">
      {items.map((it) => {
        const pct = (it.count / max) * 100;
        const bar = (
          <>
            <div
              className="absolute inset-y-0 left-0 bg-tres-action/15 group-hover:bg-tres-action/25"
              style={{ width: `${pct}%` }}
            />
            <div className="relative px-2 py-1 text-sm text-tres-text-primary">{it.label}</div>
          </>
        );
        return (
          <li key={it.uri || it.label} className="grid grid-cols-[1fr_auto] items-center gap-3">
            {it.uri ? (
              <button
                type="button"
                onClick={() => onClick(it.uri)}
                className="group relative block w-full overflow-hidden rounded text-left"
                title={it.uri}
              >
                {bar}
              </button>
            ) : (
              <div className="group relative block w-full overflow-hidden rounded text-left">
                {bar}
              </div>
            )}
            <span className="font-mono text-xs tabular-nums text-tres-text-secondary">
              {it.count.toLocaleString()}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
